import {
  VoxProductCatalogItem,
  VoxRoiCostConfigData,
  VoxRoiUsageResponse,
  VoxRoiProductUsageItem,
  VoxRoiAfterLine,
  VoxRoiBeforeProvider,
  VoxRoiResult,
} from '../models/voxRoi';

export const computeRoi = ({
  config,
  catalog,
  usage,
}: {
  config: VoxRoiCostConfigData;
  catalog: VoxProductCatalogItem[];
  usage: VoxRoiUsageResponse;
}): VoxRoiResult => {
  const { unitCount, productUsage } = usage;
  const { avgOrdersPerLoan, productCosts, beforeSelection } = config;

  const catalogByKey = new Map(catalog.map((item) => [item.productKey, item]));

  // --- BEFORE ---
  const beforeCosts = beforeSelection
    .map((key) => productCosts[key])
    .filter((cost): cost is number => cost !== undefined);
  const providerCount = beforeSelection.length;
  const avgCostPerOrder = beforeCosts.length > 0
    ? beforeCosts.reduce((sum, cost) => sum + cost, 0) / beforeCosts.length
    : 0;
  const avgCostPerLoan = avgCostPerOrder * avgOrdersPerLoan;
  const beforeTotalSpend = avgCostPerLoan * unitCount;

  const beforeProviders: VoxRoiBeforeProvider[] = beforeSelection.map((key) => {
    const catalogItem = catalogByKey.get(key);
    return {
      productKey: key,
      vendor: catalogItem?.vendor ?? 'Unknown',
      productDisplayName: catalogItem?.productDisplayName ?? key,
      costPerOrder: productCosts[key],
    };
  });

  // --- AFTER ---
  const unmappedUsage: VoxRoiProductUsageItem[] = [];
  const productsMissingCost: string[] = [];
  const afterLines: VoxRoiAfterLine[] = [];
  let afterTotalSpend = 0;

  for (const usageItem of productUsage) {
    const { productKey, successCount } = usageItem;
    const catalogProduct = catalogByKey.get(productKey);

    if (!catalogProduct) {
      unmappedUsage.push(usageItem);
      continue;
    }

    const unitCost = productCosts[productKey];
    if (unitCost === undefined && !productsMissingCost.includes(productKey)) {
      productsMissingCost.push(productKey);
    }

    const cost = unitCost ?? 0;
    const lineTotal = cost * successCount;
    afterTotalSpend += lineTotal;

    afterLines.push({
      productKey,
      vendor: catalogProduct.vendor,
      productDisplayName: catalogProduct.productDisplayName,
      successCount,
      unitCost: cost,
      lineTotal,
    });
  }

  const afterAvgCostPerLoan = unitCount > 0 ? afterTotalSpend / unitCount : 0;

  // --- SAVINGS ---
  const savingsTotal = beforeTotalSpend - afterTotalSpend;
  const savingsPct = beforeTotalSpend > 0
    ? savingsTotal / beforeTotalSpend
    : null;
  const savingsPerLoan = avgCostPerLoan - afterAvgCostPerLoan;

  return {
    unitCount,
    before: { avgCostPerOrder, avgOrdersPerLoan, avgCostPerLoan, totalSpend: beforeTotalSpend, providerCount, providers: beforeProviders },
    after: { totalSpend: afterTotalSpend, avgCostPerLoan: afterAvgCostPerLoan, lines: afterLines },
    savings: { total: savingsTotal, pct: savingsPct, perLoan: savingsPerLoan },
    warnings: { unmappedUsage, productsMissingCost },
  };
};
